import { NfcCardTransactionCriteria, NfcCardTransactionsPage, fetchCardTransactions } from 'lib/models/nfcCards';
import { useCallback, useEffect, useRef, useState } from 'react';

const emptyPage: NfcCardTransactionsPage = { elements: [], total: 0, failed: false };

/**
 * One page of the movements of a card, narrowed by `criteria`, newest first, as the API
 * answers it. Changing the filters or the page asks the API again; so does `refetch`, after
 * a load, a reset, or to see what the bar charged.
 */
function useNfcCardTransactions(
	cardId: string,
	criteria: NfcCardTransactionCriteria,
	page: number,
	rowsPerPage: number,
) {
	const [loading, setLoading] = useState<boolean>(false);

	const [transactions, setTransactions] = useState<NfcCardTransactionsPage>(emptyPage);

	// Only the newest request is shown: an older one answering late would put back a page
	// or a filter the operator already left
	const lastRequest = useRef(0);

	const refetch = useCallback(async () => {
		const request = ++lastRequest.current;

		setLoading(true);
		const response = await fetchCardTransactions(cardId, criteria, page, rowsPerPage);

		if (request !== lastRequest.current) return;

		setTransactions(response);
		setLoading(false);
	}, [cardId, criteria, page, rowsPerPage]);

	useEffect(() => {
		refetch();
	}, [refetch]);

	return {
		loading,
		transactions: transactions.elements,
		total: transactions.total,
		failed: transactions.failed,
		refetch,
	};
}

export default useNfcCardTransactions;
